/* Welke databronnen een locatie heeft, afgeleid uit haar eigen assets (F9.3).
 *
 * In greenvision-ems was dit één constante voor de hele bundel: `solis: true,
 * zympler: false`. Met één klant klopte dat; in het CRM zou `zympler: true` het
 * voor élke locatie aanzetten, ook voor een locatie zonder batterij of lader.
 *
 * Hier volgt het uit ems.asset.bron_platform, per locatie, en in drie lagen:
 *
 *   aanwezig  er staat een actieve asset met dit platform (niet 'handmatig')
 *   gelezen   aanwezig ÉN de datalaag van dit CRM leest de velden van dat
 *             platform werkelijk (GELEZEN_VELDEN hieronder)
 *   verwacht  een asset zonder bron ('handmatig') noemt in meta.verwachte_bron
 *             het platform waar zijn metingen straks vandaan komen
 *
 * Alleen `gelezen` zet iets aan in de tekening. Het verschil met `aanwezig` is
 * precies de stille fout die dit project vaker had: zet iemand morgen een
 * batterij met bron_platform 'zympler' in ems.asset, dan staat Zympler
 * "aanwezig", maar leesEms.ts haalt alleen pv_vermogen op. Zou dat de
 * vraagkant aanzetten, dan tekent het dashboard nullen alsof het metingen zijn.
 */

export type BronPlatform = 'solis' | 'zympler' | 'gridsense' | 'handmatig' | 'plugfuse';

/* De waarden van de enum ems.bron_platform, gemeten 12-09-2026; plugfuse sinds migratie 111. */
export const BRON_PLATFORMS: readonly BronPlatform[] = ['solis', 'zympler', 'gridsense', 'handmatig', 'plugfuse'];

/* De enum ems.asset_type, gemeten 12-09-2026. */
export const ASSET_TYPES = ['omvormer', 'batterij', 'lader', 'gun', 'netmeter', 'submeter', 'warmtepomp', 'generator', 'onbekend'] as const;

/* Hoe een platform op het scherm heet. Een naam, geen klantgegeven. */
export const PLATFORM_NAAM: Record<string, string> = {
  solis: 'SolisCloud',
  zympler: 'Zympler',
  gridsense: 'GridSense',
  handmatig: 'handmatig',
  plugfuse: 'Plugfuse',
};

/**
 * Welke velden de datalaag (leesEms.ts) per platform werkelijk ophaalt. Dit is
 * een eigenschap van de code, niet van een klant: komt er een ophaalslag voor
 * Zympler bij, dan komt Zympler hier bij, voor iedereen tegelijk.
 */
export const GELEZEN_VELDEN: Readonly<Record<string, readonly string[]>> = Object.freeze({
  /* De zon van de omvormers, en sinds 30-09-2026 ook de vraagkant van een
     hybride omvormer (Huigsloterdijk): batterij, net en verbruik onder dezelfde
     veldnamen als Zympler. Die worden al opgehaald (VELDEN in leesEms.ts). */
  solis: Object.freeze([
    'pv_vermogen', 'bat_soc', 'bat_laden', 'bat_ontladen', 'net_import', 'net_export',
    'bat_laden_kwh', 'bat_ontladen_kwh', 'net_import_kwh', 'net_export_kwh', 'verbruik', 'verbruik_kwh',
  ]),
  /* Moet gelijk lopen met VELDEN_ZYMPLER in crm/data/leesEms.ts: die lijst
     bepaalt wat de datalaag ophaalt, deze bepaalt of de tekening wordt
     aangezet. Loopt het uit elkaar, dan tekent het dashboard nullen alsof het
     metingen zijn. tests/ems/velden.node.test.mjs bewaakt dat. */
  zympler: Object.freeze([
    'bat_soc', 'bat_energie', 'bat_laden', 'bat_ontladen', 'net_import', 'net_export',
    'bat_laden_kwh', 'bat_ontladen_kwh', 'net_import_kwh', 'net_export_kwh',
    /* de balans van de locatie (ingest: /telemetry/locations). De delen van de
       verdeling alleen als vermogen; de dagtotalen zijn vermogen/4, zie vorm.ts. */
    'zon_locatie', 'zon_locatie_kwh', 'verbruik', 'verbruik_kwh', 'bat_soc_groen', 'bat_soc_grijs',
    'zon_naar_pand', 'zon_naar_onbekend', 'zon_naar_lader', 'zon_naar_bat', 'zon_naar_net',
    'bat_naar_pand', 'bat_naar_onbekend', 'bat_naar_lader', 'bat_naar_net',
    'net_naar_pand', 'net_naar_lader', 'net_naar_bat',
  ]),
  /* Het laadvermogen per laadpunt uit de laadsessies van de CPMS (ADR 0039).
     Moet gelijk lopen met VELDEN_PLUGFUSE in crm/data/leesEms.ts. */
  plugfuse: Object.freeze(['laad_vermogen']),
});

export function platformNaam(p: string | null | undefined): string {
  if (!p) return 'onbekende bron';
  return PLATFORM_NAAM[p] ?? p;
}

export interface Bronnen {
  aanwezig: string[];
  gelezen: string[];
  verwacht: string[];
  /* assets waarvan bron_platform ontbreekt of geen bekende waarde is */
  onbekend: string[];
}

/**
 * @param assets  actieve assets van één locatie, met bron_platform en meta
 * @param gelezenVelden  alleen voor toetsen: wat de datalaag zou lezen
 */
export function leidBronnenAf(assets: any[], gelezenVelden: Readonly<Record<string, readonly string[]>> = GELEZEN_VELDEN): Bronnen {
  const aanwezig = new Set<string>();
  const verwacht = new Set<string>();
  const onbekend: string[] = [];
  for (const a of assets || []) {
    if (a?.actief === false) continue;
    const p = a?.bron_platform;
    if (!BRON_PLATFORMS.includes(p)) {
      onbekend.push(String(a?.naam ?? a?.id ?? '?'));
      continue;
    }
    if (p === 'handmatig') {
      const v = a?.meta?.verwachte_bron;
      if (typeof v === 'string' && BRON_PLATFORMS.includes(v as BronPlatform) && v !== 'handmatig') verwacht.add(v);
      continue;
    }
    aanwezig.add(p);
  }
  const volgorde = (s: Set<string>) => BRON_PLATFORMS.filter((p) => s.has(p));
  const aanw = volgorde(aanwezig);
  return {
    aanwezig: aanw,
    gelezen: aanw.filter((p) => (gelezenVelden[p]?.length ?? 0) > 0),
    verwacht: volgorde(verwacht).filter((p) => !aanwezig.has(p)),
    onbekend,
  };
}

/* Vaste tekst voor alles wat op een bron wacht, zodat het overal gelijk leest. */
export const NIET_GEKOPPELD = 'nog niet gekoppeld';

/* Vaste tekst voor een gegeven dat per locatie ingevuld hoort te zijn en dat niet is. */
export const NIET_INGEVULD = 'niet ingevuld';
